import { useContext } from "react";
import { Subscription } from "rxjs";
import {
  observable,
  action,
  computed,
  runInAction,
  reaction,
  IReactionDisposer,
} from "mobx";
import { SessionEntity, DivisionEntity } from "@citychallenge/api-public";
import { servesPages } from "@citychallenge/common";
import { ApiService } from "../api";
import { UserService, LoginError } from "../user";
import { MessageService } from "../message";
import { StorageService } from "../storage";
import { ServiceInterface } from "../service.interface";
import {
  GameModel,
  PlayerGameModel,
  DivisionModel,
  GameStore,
  ArticleModel,
  DivisionData,
  OnDemandPlaylistModel,
} from "../../models";
import {
  LocalizedError,
  NotFoundError,
  StoryNotFoundError,
} from "../../errors";
import { TransTokens } from "../../i18n/trans-tokens";
import { SessionService } from "../session";
import { createContext } from "react";
import { I18nService } from "../i18n";
import { Loadable, LoadableRequestOptions } from "../../loadable";
import { GameSessionModel } from "../../player";
import { DeviceService } from "../device";

interface DivisionConnectParams {
  entity: DivisionEntity;

  startSession?: boolean;
}
interface DivisionSessionSync {
  timestamp: number;
  data: SessionEntity[];
}

const tokens = TransTokens.create({
  notRecognized: "This code is not recognized by the app.",
  errorWithCode: "An error occurred while checking this code: {{error}}",
  noActiveDivision: "Not connected to any company.",
  invalidDomain: "Please enter an approved email address to login.",
  invalidDomainWithDivision:
    "Your current account is not approved by '{{division}}'. \n Please log in with a different email address.",
});

export const divisionTokens = tokens;

export const DivisionServiceContext = createContext<DivisionService>(
  null as any
);

export const useActiveDivision = () => {
  return useContext(DivisionServiceContext)?.activeDisplayDivision;
};

export class DivisionService implements ServiceInterface {
  public context = DivisionServiceContext;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private storageService: StorageService,
    private messageService: MessageService,
    private sessionService: SessionService,
    private i18nService: I18nService,
    private deviceService: DeviceService
  ) {}

  public KEY_ACTIVE_DIVISION = "ACTIVE_DIVISON";

  /**
   * Internal in milliseconds for which the app refresh the division after reopening
   * the application
   */

  private subscriptions: Subscription[] = [];

  private _lastUpdate = this.storageService.getPreference<number>(
    "DIVISION_LAST_UPDATE"
  );

  /**
   * Current active division. Use this property for conditional routing.
   */
  @observable
  public activeDivision?: DivisionModel;

  @observable
  private _previousActiveDivision?: DivisionModel;

  /**
   * This property is meant for use in components that display active division,
   * it holds previous active division if there was any,
   * so that elements do not disappear during pop animations.
   */
  @computed
  public get activeDisplayDivision() {
    return this.activeDivision || this._previousActiveDivision;
  }

  @computed
  public get activeDisplayValues() {
    const currentLanguage = this.i18nService.preferredLanguage;
    const translation = this.activeDisplayDivision?.translations?.find(
      (t) => t.language?.id === currentLanguage
    );
    const division = this.activeDisplayDivision;

    return {
      description: translation?.description || division?.description,
      experiencesTabTitle:
        translation?.experiencesTabTitle || division?.experiencesTabTitle,
      articlesTabTitle:
        translation?.articlesTabTitle || division?.articlesTabTitle,
      eventsTabTitle: translation?.eventsTabTitle || division?.eventsTabTitle,
      experiencesWelcomeText:
        translation?.experiencesWelcomeText || division?.experiencesWelcomeText,
      onDemandTabTitle:
        translation?.onDemandTabTitle || division?.onDemandTabTitle,
      mapBasedTitle: translation?.mapBasedTitle || division?.mapBasedTitle,
      contactButtonTitle:
        translation?.contactButtonTitle || division?.contactButtonTitle,
      contactPageText:
        translation?.contactPageText || division?.contactPageText,
      contactPageImage:
        translation?.contactPageImage || division?.contactPageImage,
    };
  }

  @computed
  public get activeDisplayDivisionDescription() {
    return this.activeDisplayValues.description;
  }

  public setActiveDivision = (division: DivisionData, fromCache = false) => {
    if (this.activeDivision && this.activeDivision.id === division?.id) {
      this.updateDivision(this.activeDivision, division);
      return this.activeDivision;
    }

    const model = this._setActiveDivision(division);

    if (!fromCache && !servesPages(model)) {
      this.resetPreferredLanguage(model);
    }

    // New division connect, reset preferred language to the division base language
    this._updateDivisionStorage(division);

    return model;
  };

  private resetPreferredLanguage = (model: DivisionModel) => {
    const preferred = this.deviceService?.getPreferredLanguages();
    if (preferred?.length && model.supportedLanguages.length) {
      const userLanguages = preferred.map((pref) => pref.short);

      for (const lang of userLanguages) {
        if (model.supportedLanguages.includes(lang)) {
          this.i18nService.setPreferredLanguage(lang);
          return;
        }
      }

      this.i18nService.setPreferredLanguage(model.supportedLanguages[0]);
    }
  };

  public clearActiveDivision = () => {
    this.storageService.removeItem(this.KEY_ACTIVE_DIVISION);
    this._setActiveDivision(undefined);
  };

  private _setActiveDivision(division: DivisionData): DivisionModel;
  private _setActiveDivision(division: undefined): undefined;
  @action
  private _setActiveDivision(division: DivisionData | undefined) {
    if (this.activeDivision) this._previousActiveDivision = this.activeDivision;

    this.activeDivision = division ? new DivisionModel(division) : undefined;

    return this.activeDivision;
  }

  private _updateDivisionStorage(division?: DivisionData) {
    return this.storageService.setItem(
      this.KEY_ACTIVE_DIVISION,
      JSON.stringify(division)
    );
  }

  public async checkActiveDivision() {
    if (this.activeDivision) return this.activeDivision;
    const value = this.storageService.getItem(this.KEY_ACTIVE_DIVISION);

    if (value) {
      this.setActiveDivision(JSON.parse(value), true);
    }
    return this.activeDivision;
  }

  public async serviceWillMount() {
    await this._lastUpdate.ready$;
    // Set last update value equal to now if it does not exist yet
    if (!this._lastUpdate.value) this._lastUpdate.setValue(+new Date());

    // A refused sign-in is re-judged the moment a new one lands, whichever
    // screen it came through — the magic link never cleared it, so the classic
    // dashboard kept showing "log in again" to members who just had.
    this.authErrorDisposer = reaction(
      () => this.userService.loggedIn,
      (loggedIn) => {
        if (loggedIn) this.revalidateAuthError();
      }
    );

    await this.checkActiveDivision();
  }

  public serviceWillUnmount() {
    for (const subscription of this.subscriptions) subscription.unsubscribe();
    this.authErrorDisposer?.();
  }

  private authErrorDisposer?: IReactionDisposer;

  /**
   * A sign-in is not proof of access — the same account can come back still
   * not a member — so the refusal is cleared optimistically and then re-asked
   * of the project. Optimistic, because members are the common case and
   * leaving "log in again" up until the answer returns would flash it at
   * someone who just did; re-asked, because a visitor the project still turns
   * away belongs on that screen, not in a shell full of failing content.
   *
   * Only fires for a completed sign-in: the MFA and account-setup steps hold
   * their interim token themselves and never store it.
   */
  private async revalidateAuthError() {
    this.removeAuthError();

    const division = this.activeDivision;
    if (!division?.forceAuthentication) return;

    try {
      await this.authStatus(division);
    } catch (e) {
      // Anything else — the network, the project itself — the content
      // requests will surface on their own; this check is only about access.
      if (!(e instanceof LoginError.UnauthenticatedError)) return;

      runInAction(() => {
        if (this.activeDivision === division) division.authError = true;
      });
    }
  }

  public async getDivisionQRCode() {
    if (!this.activeDivision) return;

    const { data } = await this.apiService.clientPublic.getDivisionQRCode(
      this.activeDivision.id
    );

    return data;
  }

  public async getDivisionContact(divisionId: string) {
    const { data } = await this.apiService.clientPublic.contactForDivision(
      divisionId
    );
    return data;
  }

  public async updateDivision(division: DivisionModel, data: DivisionData) {
    this._lastUpdate.setValue(+new Date());
    this._updateDivisionStorage(data);
    division.update(data);
  }

  public async removeAuthError() {
    if (!this.activeDivision) return;

    runInAction(() => {
      if (this.activeDivision) {
        this.activeDivision.authError = false;
      }
    });
  }

  // check if user can have access to the division that is scanned
  public async authStatus(division: Pick<DivisionEntity, "id" | "name">) {
    // Not gated on `activeDivision`: on a first visit nothing is cached yet,
    // and skipping the check there meant a dead session was only noticed once
    // the content requests failed — and only ever noticed on repeat visits.
    if (!this.userService.user) return undefined;

    try {
      return await this.userService.authStatus(division.id);
    } catch (e) {
      if (
        e instanceof LoginError.InvalidDomainError ||
        e instanceof LoginError.InvalidEmailError
      ) {
        this.messageService.show({
          message: tokens.invalidDomainWithDivision,
          values: {
            division: division.name,
          },
          type: "danger",
          duration: 10000,
        });
        return undefined;
      }

      throw e;
    }
  }

  public async passwordlessLogin(email: string) {
    if (!this.activeDivision)
      throw new LocalizedError(undefined, tokens.noActiveDivision);

    try {
      return await this.userService.passwordlessLogin(
        email,
        this.activeDivision.id
      );
    } catch (e) {
      this.rethrowInvalidDomain(email, e);
      throw e;
    }
  }

  public rethrowInvalidDomain(email: string, e: any) {
    if (
      e instanceof LoginError.InvalidDomainError ||
      e instanceof LoginError.InvalidEmailError
    ) {
      throw new LocalizedError(e, tokens.invalidDomain, {
        domain: email.split("@")[1],
      });
    }
  }

  public connectToDivision = async ({ entity }: DivisionConnectParams) => {
    try {
      // Because the division is usually set through injection through the HTML
      // page (and passed in with the entity option). We only want to load
      // the division remotely if entity was not set. This is the case for
      // update/refresh requests or the development environment.
      const data = entity;

      this.sessionService.clearActiveSession();

      let authError = false;
      if (data.forceAuthentication) {
        try {
          await this.authStatus(data);
        } catch (e) {
          if (e instanceof LoginError.UnauthenticatedError) {
            authError = true;
          }
        }
      }

      const model = this.setActiveDivision({ ...data, authError });

      // Reset the last update timer
      this._lastUpdate.setValue(+new Date());

      return model;
    } catch (e) {
      if (e.response?.status === 404) {
        this.messageService.show({
          message: tokens.notRecognized,
          type: "danger",
        });
      } else {
        this.messageService.show({
          message: tokens.errorWithCode,
          values: {
            error: e.message,
          },
          type: "danger",
        });
      }

      throw e;
    }
  };

  @observable
  public divisionGames: {
    [key: string]: Loadable<{ games: GameModel[] }>;
  } = {};

  private async fetchDivisionGames(divisionId: string) {
    const games = await this.apiService.clientPublic.gamesOfDivision(
      divisionId
    );

    const mappedGames = games.data.map((game) => {
      return new GameModel(
        game,
        this.storageService.getPreference(`unlocked_${game.id}`)
      );
    });

    return { games: mappedGames };
  }

  public loadableDivisionStore = () => {
    const division = this.activeDisplayDivision;

    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    if (division.loadableGameStore) return division.loadableGameStore;

    division.loadableGameStore = Loadable.create(
      async () => {
        const { gameStore } = division;
        const { games } = await this.fetchDivisionGames(division.id);

        gameStore.setGames(games);

        // Hydrate game store with sessions on refresh
        this.hydrateSessions(gameStore);

        return gameStore;
      },
      {
        refreshInterval: 15 * 60, // Refresh after 15 min of inactivity
        translatedContent: true,
      }
    );

    return division.loadableGameStore;
  };

  private findGame(list: GameModel[], gameId: string) {
    return list.find((g) => g.id == gameId);
  }

  private async fetchDivisionGame(divisionId: string, gameId: string) {
    try {
      const { data } = await this.apiService.clientPublic.gameOfDivision(
        divisionId,
        gameId
      );

      return GameModel.fromPlayerPayload(
        data,
        this.storageService.getPreference(`unlocked_${data.id}`)
      );
    } catch (e: any) {
      if (e?.response?.status === 404) throw new StoryNotFoundError();
      throw e;
    }
  }

  /**
   * Resolves a single story. The overview's store is reused when it already
   * holds the story, but opening a story directly — a share link — fetches only
   * that story rather than pulling the division's whole catalogue to the client.
   */
  private async fetchGame(gameId: string, options?: LoadableRequestOptions) {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    if (!options?.reloadData) {
      const loaded = division.loadableGameStore?.value;
      const cached = loaded && this.findGame(loaded.games, gameId);
      if (cached) return cached;
    }

    const game = await this.fetchDivisionGame(division.id, gameId);

    // Sessions are hydrated per division; a store of one lets the story pick up
    // its own "resume" state without materialising the rest of the catalogue.
    await this.hydrateSessions(new GameStore([game]));

    return game;
  }

  /**
   * Typed as the narrowed player view: a share link resolves through
   * `fetchDivisionGame`, whose payload carries none of the catalogue fields.
   * The cached branch does hand back a full `GameModel`, but callers of this
   * loadable cannot know which branch they got, so the narrower of the two is
   * the only type that holds for both.
   */
  public loadableGame(gameId: string): Loadable<PlayerGameModel> {
    const value = this.activeDisplayDivision?.loadableGameStore?.value;
    const defaultValue = value ? this.findGame(value.games, gameId) : undefined;

    return Loadable.create((options) => this.fetchGame(gameId, options), {
      defaultValue,
      refreshInterval: 15 * 60, // Refresh after 15 min of inactivity
      translatedContent: true,
    });
  }

  /**
   * Hydrate games with sessions fetched from the server for this user
   */
  private async hydrateSessions(gameStore: GameStore) {
    const activeDivision = this.activeDisplayDivision;
    if (!activeDivision)
      throw new LocalizedError(undefined, tokens.noActiveDivision);

    const preference = this.storageService.getPreference<DivisionSessionSync>(
      `SESSIONS_LOADED_${activeDivision.id}`,
      { timestamp: 0, data: [] }
    );

    const { timestamp, data } = await preference.ready$;

    let sessions: SessionEntity[];
    if (timestamp === 0 && this.userService.loggedIn) {
      const response = await this.apiService.clientPrivate.sessionsForDivision(
        activeDivision.id
      );

      sessions = response.data;
      preference.setValue({ timestamp: +new Date(), data: sessions });
    } else {
      sessions = data;
    }

    const allSessions: GameSessionModel[] = [];
    const restartIfOutdated = !!activeDivision.showLatestStoryVersion;

    for (const game of gameStore.games) {
      const model = this.sessionService.modelForGame(game, sessions);
      if (!model) continue;

      // When the division has opted into always showing the latest story
      // version, discard any stored session that was built from an older
      // release so the overview does not show a stale "Resume" button and
      // the next open starts a fresh session.
      if (
        restartIfOutdated &&
        this.sessionService.isSessionOutdated(
          model,
          game.release?.releaseVersion
        )
      ) {
        this.sessionService.clearGameSessionDataForGame(game.id);
        continue;
      }

      allSessions.push(model);
    }

    gameStore.setSessions(allSessions);
  }

  /**
   * Article loaders
   */
  public fetchDivisionArticles = async (divisionId: string) => {
    const { data } = await this.apiService.clientPublic.articlesOfDivision(
      divisionId
    );

    return data.map((article) => {
      return new ArticleModel(article);
    });
  };

  public getArticleStore = () => {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    return division.articleStore;
  };
  public loadableArticleStore = () => {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    if (division.loadableArticleStore) return division.loadableArticleStore;

    division.loadableArticleStore = Loadable.create(
      async () => {
        const { articleStore } = division;
        const articles = await this.fetchDivisionArticles(division.id);

        articleStore.setArticles(articles);

        return articleStore;
      },
      {
        translatedContent: true,
        refreshInterval: 15 * 60, // Refresh
      }
    );

    return division.loadableArticleStore;
  };

  public loadableArticle(articleId: string) {
    const { ensuredValue } = this.loadableArticleStore();

    return Loadable.create(async () => {
      const store = await ensuredValue();
      const value = store.findById(articleId);
      if (!value) throw new NotFoundError();
      return value;
    });
  }

  /**
   * OnDemand loaders
   */
  public fetchDivisionOnDemandPlaylists = async (divisionId: string) => {
    const { data } =
      await this.apiService.clientPublic.onDemandPlaylistsOfDivision(
        divisionId
      );

    return data.map((playlist) => {
      return new OnDemandPlaylistModel(playlist);
    });
  };

  public getOnDemandStore = () => {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    return division.onDemandStore;
  };

  public loadableOnDemandStore = () => {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    if (division.loadableOnDemandStore) return division.loadableOnDemandStore;

    division.loadableOnDemandStore = Loadable.create(
      async () => {
        const { onDemandStore } = division;
        const playlists = await this.fetchDivisionOnDemandPlaylists(
          division.id
        );

        onDemandStore.setPlaylists(playlists);

        return onDemandStore;
      },
      {
        translatedContent: true,
        refreshInterval: 15 * 60, // Refresh after 15 min of inactivity
      }
    );

    return division.loadableOnDemandStore;
  };

  public loadableOnDemandPlaylist(playlistId: string) {
    const { ensuredValue } = this.loadableOnDemandStore();

    return Loadable.create(async () => {
      const store = await ensuredValue();
      const value = store.findById(playlistId);
      if (!value) throw new NotFoundError();
      return value;
    });
  }
}
